/**
 * Mock templates shown when no Medplum server data is available
 * (e.g. empty list or fetch error) so the UI still displays sample content.
 * Organized by Template Library tab (see product taxonomy).
 */
import type { Questionnaire } from '@medplum/fhirtypes';
import type { TemplateCategory, TemplateListItem } from './types';
import { TEMPLATE_CATEGORY_SYSTEM } from './types';
import { ITEM_WEIGHT_URL, SCORING_CONFIG_URL } from './scoringTypes';
import type { ScoringConfiguration } from './scoringTypes';

function mockQuestionnaire(
  id: string,
  title: string,
  category: TemplateCategory,
  item: Questionnaire['item'] = [],
  scoringConfig?: ScoringConfiguration,
): Questionnaire {
  const extensions: Questionnaire['extension'] = [];
  if (scoringConfig) {
    extensions.push({ url: SCORING_CONFIG_URL, valueString: JSON.stringify(scoringConfig) });
  }
  return {
    resourceType: 'Questionnaire',
    id,
    title,
    status: 'draft',
    meta: {
      tag: [
        {
          system: TEMPLATE_CATEGORY_SYSTEM,
          code: category,
        },
      ],
    },
    item: item.length ? item : undefined,
    extension: extensions.length > 0 ? extensions : undefined,
  };
}

function weightedOption(display: string, code: string, weight: number) {
  return {
    valueCoding: {
      code,
      display,
      extension: [{ url: ITEM_WEIGHT_URL, valueDecimal: weight }],
    },
  };
}

const PHQ_LIKERT = [
  weightedOption('0 — Not at all', '0', 0),
  weightedOption('1 — Several days', '1', 1),
  weightedOption('2 — More than half the days', '2', 2),
  weightedOption('3 — Nearly every day', '3', 3),
];

const MOCK_PHQ9_SCORING: ScoringConfiguration = {
  outputs: [{
    id: 'phq9-total', label: 'PHQ-9 Total Score', codeSystem: 'http://loinc.org',
    codeValue: '44261-6', codeDisplay: 'PHQ-9 total score', valueType: 'integer',
    includedItemLinkIds: [], transform: 'sum',
  }],
  bands: {
    'phq9-total': [
      { id: 'b1', label: 'Minimal', low: 0, high: 4 },
      { id: 'b2', label: 'Mild', low: 5, high: 9 },
      { id: 'b3', label: 'Moderate', low: 10, high: 14 },
      { id: 'b4', label: 'Moderately Severe', low: 15, high: 19 },
      { id: 'b5', label: 'Severe', low: 20, high: 27 },
    ],
  },
  riskFlags: [{
    id: 'rf1', type: 'item-threshold', itemLinkId: 'q10', threshold: 1,
    comparison: '>=', flagCode: 'self-harm-risk',
    flagDisplay: 'Risk flag: self-harm ideation (PHQ-9 item 9)',
  }],
};

const MOCK_GAD7_SCORING: ScoringConfiguration = {
  outputs: [{
    id: 'gad7-total', label: 'GAD-7 Total Score', codeSystem: 'http://loinc.org',
    codeValue: '70274-6', codeDisplay: 'GAD-7 total score', valueType: 'integer',
    includedItemLinkIds: [], transform: 'sum',
  }],
  bands: {
    'gad7-total': [
      { id: 'b1', label: 'Minimal', low: 0, high: 4 },
      { id: 'b2', label: 'Mild', low: 5, high: 9 },
      { id: 'b3', label: 'Moderate', low: 10, high: 14 },
      { id: 'b4', label: 'Severe', low: 15, high: 21 },
    ],
  },
  riskFlags: [],
};

const headerExtension = [
  {
    url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
    valueCodeableConcept: {
      coding: [{ system: 'http://hl7.org/fhir/questionnaire-item-control', code: 'header' }],
    },
  },
];

function simpleTextItem(label: string): Questionnaire['item'] {
  return [{ linkId: 'body', type: 'text', text: label }];
}

function mockRow(
  id: string,
  title: string,
  description: string,
  category: TemplateCategory,
  item: Questionnaire['item'],
  status: 'active' | 'draft' = 'draft',
): TemplateListItem {
  const now = new Date().toISOString();
  return {
    id,
    title,
    description,
    status,
    category,
    lastUpdated: now,
    versionId: '1',
    isDefault: false,
    inUse: status === 'active',
    resource: mockQuestionnaire(id, title, category, item),
  };
}

export function getMockTemplates(): TemplateListItem[] {
  const now = new Date().toISOString();
  const raw: TemplateListItem[] = [
    // ---- Appointments ----
    mockRow('mock-appt-1', 'Screening Note (Demo)', 'Brief screening documentation for initial contact.', 'appointments', simpleTextItem('Screening summary'), 'active'),
    mockRow('mock-appt-2', 'Consultation Note (Demo)', 'Consultation session documentation.', 'appointments', simpleTextItem('Consultation details'), 'active'),
    mockRow('mock-appt-3', 'Intake Note (Demo)', 'Comprehensive intake assessment.', 'appointments', simpleTextItem('Intake content'), 'active'),
    mockRow('mock-note-1', 'Progress Note (Demo)', 'Standard progress note with Subjective, Objective, Assessment, Plan', 'appointments', [
      { linkId: 's1', type: 'display', text: 'Session summary', extension: headerExtension },
      { linkId: 'q1', type: 'text', text: 'Subjective' },
      { linkId: 'q2', type: 'text', text: 'Objective' },
      { linkId: 'q3', type: 'text', text: 'Assessment and plan' },
    ], 'active'),
    mockRow('mock-note-2', 'SOAP Note (Demo)', 'Structured SOAP format for clinical documentation', 'appointments', [
      { linkId: 's1', type: 'display', text: 'SOAP Note', extension: headerExtension },
      { linkId: 'q1', type: 'text', text: 'Subjective' },
      { linkId: 'q2', type: 'text', text: 'Objective' },
      { linkId: 'q3', type: 'text', text: 'Assessment' },
      { linkId: 'q4', type: 'text', text: 'Plan' },
    ], 'active'),
    mockRow('mock-appt-4', 'Crisis Session Note (Demo)', 'Documentation for urgent or crisis sessions.', 'appointments', simpleTextItem('Crisis session narrative'), 'draft'),
    mockRow('mock-appt-5', 'Psychotherapy Notes (Not exported) (Demo)', 'Psychotherapy process notes; excluded from standard export.', 'appointments', simpleTextItem('Psychotherapy notes'), 'draft'),

    // ---- Measures ----
    {
      id: 'mock-measure-1',
      title: 'PHQ-9 (Demo)',
      description: 'Patient Health Questionnaire-9 for depression screening',
      status: 'active',
      category: 'measures',
      lastUpdated: now,
      versionId: '1',
      isDefault: false,
      inUse: true,
      resource: mockQuestionnaire('mock-measure-1', 'PHQ-9 (Demo)', 'measures', [
        { linkId: 'q1', type: 'display', text: 'Over the last 2 weeks, how often have you been bothered by any of the following problems?' },
        { linkId: 'q2', type: 'choice', text: 'Little interest or pleasure in doing things', answerOption: PHQ_LIKERT },
        { linkId: 'q3', type: 'choice', text: 'Feeling down, depressed, or hopeless', answerOption: PHQ_LIKERT },
        { linkId: 'q4', type: 'choice', text: 'Trouble falling or staying asleep, or sleeping too much', answerOption: PHQ_LIKERT },
        { linkId: 'q5', type: 'choice', text: 'Feeling tired or having little energy', answerOption: PHQ_LIKERT },
        { linkId: 'q6', type: 'choice', text: 'Poor appetite or overeating', answerOption: PHQ_LIKERT },
        { linkId: 'q7', type: 'choice', text: 'Feeling bad about yourself — or that you are a failure', answerOption: PHQ_LIKERT },
        { linkId: 'q8', type: 'choice', text: 'Trouble concentrating on things', answerOption: PHQ_LIKERT },
        { linkId: 'q9', type: 'choice', text: 'Moving or speaking slowly, or being fidgety/restless', answerOption: PHQ_LIKERT },
        { linkId: 'q10', type: 'choice', text: 'Thoughts that you would be better off dead, or of hurting yourself', answerOption: PHQ_LIKERT },
      ], MOCK_PHQ9_SCORING),
    },
    {
      id: 'mock-measure-2',
      title: 'GAD-7 (Demo)',
      description: 'Generalized Anxiety Disorder 7-item scale',
      status: 'active',
      category: 'measures',
      lastUpdated: now,
      versionId: '1',
      isDefault: false,
      inUse: true,
      resource: mockQuestionnaire('mock-measure-2', 'GAD-7 (Demo)', 'measures', [
        { linkId: 'q1', type: 'display', text: 'Over the last 2 weeks, how often have you been bothered by the following problems?', extension: headerExtension },
        { linkId: 'q2', type: 'choice', text: 'Feeling nervous, anxious, or on edge', answerOption: PHQ_LIKERT },
        { linkId: 'q3', type: 'choice', text: 'Not being able to stop or control worrying', answerOption: PHQ_LIKERT },
        { linkId: 'q4', type: 'choice', text: 'Worrying too much about different things', answerOption: PHQ_LIKERT },
        { linkId: 'q5', type: 'choice', text: 'Trouble relaxing', answerOption: PHQ_LIKERT },
        { linkId: 'q6', type: 'choice', text: 'Being so restless that it\'s hard to sit still', answerOption: PHQ_LIKERT },
        { linkId: 'q7', type: 'choice', text: 'Becoming easily annoyed or irritable', answerOption: PHQ_LIKERT },
        { linkId: 'q8', type: 'choice', text: 'Feeling afraid as if something awful might happen', answerOption: PHQ_LIKERT },
      ], MOCK_GAD7_SCORING),
    },
    mockRow('mock-measure-3', 'PHQ-2 (Demo)', 'Brief 2-item depression screener', 'measures', [
      { linkId: 'q1', type: 'display', text: 'Over the last 2 weeks, how often have you had little interest or pleasure in doing things?' },
      { linkId: 'q2', type: 'choice', text: 'Feeling down, depressed, or hopeless', answerOption: [
        { valueString: '0 — Not at all' },
        { valueString: '1 — Several days' },
        { valueString: '2 — More than half the days' },
        { valueString: '3 — Nearly every day' },
      ]},
    ], 'draft'),
    mockRow('mock-measure-4', 'WHO-5 (Demo)', 'WHO Well-Being Index 5-item', 'measures', [
      { linkId: 'q1', type: 'display', text: 'Over the last 2 weeks, how often have you felt cheerful and in good spirits?' },
      { linkId: 'q2', type: 'choice', text: 'I have felt calm and relaxed', answerOption: [
        { valueString: '5 — All of the time' },
        { valueString: '4 — Most of the time' },
        { valueString: '3 — More than half the time' },
        { valueString: '2 — Less than half the time' },
        { valueString: '1 — Some of the time' },
        { valueString: '0 — At no time' },
      ]},
    ], 'draft'),
    mockRow('mock-measure-5', 'PCL-5 (Demo)', 'PTSD Checklist for DSM-5', 'measures', simpleTextItem('PCL-5 items'), 'draft'),
    mockRow('mock-measure-6', 'Lab Orders (Demo)', 'Template for lab requisitions and orders.', 'measures', simpleTextItem('Labs ordered'), 'draft'),

    // ---- Treatment planning ----
    mockRow('mock-tp-1', 'Treatment Plan (Demo)', 'Master treatment plan with goals and interventions', 'treatment-planning', [
      { linkId: 's1', type: 'display', text: 'Treatment plan', extension: headerExtension },
      { linkId: 'q1', type: 'string', text: 'Diagnosis' },
      { linkId: 'q2', type: 'text', text: 'Goals' },
      { linkId: 'q3', type: 'text', text: 'Interventions' },
      { linkId: 'q4', type: 'string', text: 'Target date for review' },
    ], 'active'),
    mockRow('mock-tp-2', 'Safety Plan (Demo)', 'Safety and crisis intervention plan', 'treatment-planning', [
      { linkId: 's1', type: 'display', text: 'Safety plan', extension: headerExtension },
      { linkId: 'q1', type: 'text', text: 'Warning signs' },
      { linkId: 'q2', type: 'text', text: 'Coping strategies' },
      { linkId: 'q3', type: 'text', text: 'Emergency contacts and resources' },
    ], 'active'),
    mockRow('mock-tp-3', 'Risk Assessment (Demo)', 'Structured risk evaluation.', 'treatment-planning', simpleTextItem('Risk assessment'), 'draft'),
    mockRow('mock-tp-4', 'Discharge Summary (Demo)', 'Summary of care at discharge', 'treatment-planning', [
      { linkId: 's1', type: 'display', text: 'Discharge summary', extension: headerExtension },
      { linkId: 'q1', type: 'text', text: 'Reason for discharge' },
      { linkId: 'q2', type: 'text', text: 'Summary of treatment' },
      { linkId: 'q3', type: 'text', text: 'Recommendations and follow-up' },
    ], 'active'),
    mockRow('mock-tp-5', 'Transfer Request (Demo)', 'Request to transfer care to another provider or level.', 'treatment-planning', simpleTextItem('Transfer rationale'), 'draft'),
    mockRow('mock-tp-6', 'Medication Orders (Demo)', 'Medication order documentation.', 'treatment-planning', simpleTextItem('Medication orders'), 'draft'),
    mockRow('mock-tp-7', 'Chart Notes (Demo)', 'General clinical chart entries.', 'treatment-planning', simpleTextItem('Chart note'), 'draft'),
    mockRow('mock-tp-8', 'Incident Reports (Demo)', 'Incident reporting template.', 'treatment-planning', simpleTextItem('Incident description'), 'draft'),
    mockRow('mock-dx-tx-3', 'Annual Treatment Plan Review (Demo)', 'Yearly review and update of treatment plan', 'treatment-planning', [
      { linkId: 's1', type: 'display', text: 'Annual review', extension: headerExtension },
      { linkId: 'q1', type: 'text', text: 'Progress toward goals' },
      { linkId: 'q2', type: 'text', text: 'Updated goals' },
      { linkId: 'q3', type: 'text', text: 'Continued interventions' },
    ], 'draft'),
    mockRow('mock-dx-tx-4', 'Family Treatment Plan (Demo)', 'Treatment plan for family therapy', 'treatment-planning', [
      { linkId: 's1', type: 'display', text: 'Family treatment plan', extension: headerExtension },
      { linkId: 'q1', type: 'text', text: 'Family goals' },
      { linkId: 'q2', type: 'text', text: 'Family interventions' },
    ], 'draft'),

    // ---- Forms ----
    mockRow('mock-forms-1', 'Consent for Treatment (Demo)', 'General consent for mental health treatment', 'forms', [
      { linkId: 's1', type: 'display', text: 'Consent for treatment', extension: headerExtension },
      { linkId: 'q1', type: 'boolean', text: 'I consent to mental health treatment as described' },
      { linkId: 'q2', type: 'date', text: 'Date' },
    ], 'active'),
    mockRow(
      'mock-forms-consent-es',
      'Consent for Treatment (Español) (Demo)',
      'Spanish-language consent for mental health treatment (pilot / intake variant demo)',
      'forms',
      [
        { linkId: 's1', type: 'display', text: 'Consentimiento para el tratamiento', extension: headerExtension },
        { linkId: 'q1', type: 'boolean', text: 'Doy mi consentimiento para el tratamiento de salud mental descrito' },
        { linkId: 'q2', type: 'date', text: 'Fecha' },
      ],
      'active',
    ),
    mockRow('mock-forms-2', 'Release of Information (ROI) (Demo)', 'Authorization to release or obtain records', 'forms', [
      { linkId: 's1', type: 'display', text: 'Release of information', extension: headerExtension },
      { linkId: 'q1', type: 'string', text: 'Name of person/entity to release to' },
      { linkId: 'q2', type: 'string', text: 'Information to be released' },
      { linkId: 'q3', type: 'date', text: 'Expiration date' },
    ], 'active'),
    mockRow('mock-forms-intake', 'Intake Forms (Demo)', 'Standard intake packet.', 'forms', simpleTextItem('Intake responses'), 'draft'),
    mockRow('mock-forms-3', 'Custody Agreement Acknowledgment (Demo)', 'Acknowledgment of custody orders for minor clients', 'forms', [
      { linkId: 's1', type: 'display', text: 'Custody acknowledgment', extension: headerExtension },
      { linkId: 'q1', type: 'boolean', text: 'I have received a copy of the custody order' },
      { linkId: 'q2', type: 'string', text: 'Guardian signature' },
    ], 'draft'),
    mockRow('mock-forms-4', 'Telehealth Consent (Demo)', 'Consent for telehealth services', 'forms', [
      { linkId: 's1', type: 'display', text: 'Telehealth consent', extension: headerExtension },
      { linkId: 'q1', type: 'boolean', text: 'I consent to receive services via telehealth' },
      { linkId: 'q2', type: 'string', text: 'Preferred platform' },
    ], 'draft'),
    mockRow('mock-forms-legal', 'Legal Holds (Demo)', 'Documentation related to legal holds.', 'forms', simpleTextItem('Legal hold details'), 'draft'),
    mockRow('mock-forms-court', 'Court Documents (Demo)', 'Court-ordered documentation.', 'forms', simpleTextItem('Court document summary'), 'draft'),
    mockRow('mock-forms-gfe', 'Good Faith Estimates (Demo)', 'No Surprises Act good faith estimate.', 'forms', simpleTextItem('Estimate details'), 'draft'),

    // ---- Client operations ----
    mockRow('mock-co-1', 'Service Request Note (Demo)', 'Documentation for service or referral requests.', 'administrative', simpleTextItem('Service request'), 'active'),
    mockRow('mock-co-2', 'Collateral Contact Note (Demo)', 'Contact with collateral sources.', 'administrative', simpleTextItem('Collateral contact'), 'active'),
    mockRow('mock-co-3', 'Contact Attempts (Demo)', 'Log of outreach attempts.', 'administrative', simpleTextItem('Contact attempts'), 'draft'),
    mockRow('mock-co-4', 'Contact Logs (Demo)', 'Ongoing contact log.', 'administrative', simpleTextItem('Contact log'), 'draft'),
    mockRow('mock-co-5', 'Admin Notes (Not Exported) (Demo)', 'Internal admin notes excluded from export.', 'administrative', simpleTextItem('Admin note'), 'draft'),
    mockRow('mock-co-6', 'Eligibility Notes (Demo)', 'Coverage and eligibility documentation.', 'administrative', simpleTextItem('Eligibility'), 'draft'),
    mockRow('mock-co-7', 'Waitlist Entry (Demo)', 'Waitlist placement record.', 'administrative', simpleTextItem('Waitlist entry'), 'draft'),
    mockRow('mock-co-8', 'Waitlist Activity (Demo)', 'Waitlist status changes.', 'administrative', simpleTextItem('Waitlist activity'), 'draft'),
    mockRow('mock-co-9', 'Automated Emails (Not Exported) (Demo)', 'Record of automated communications.', 'administrative', simpleTextItem('Email log'), 'draft'),
    mockRow('mock-other-2', 'Referral Letter (Demo)', 'Template for external referrals', 'administrative', [
      { linkId: 's1', type: 'display', text: 'Referral', extension: headerExtension },
      { linkId: 'q1', type: 'string', text: 'Referring to (provider/specialty)' },
      { linkId: 'q2', type: 'text', text: 'Reason for referral' },
      { linkId: 'q3', type: 'text', text: 'Relevant history' },
    ], 'active'),
    mockRow('mock-other-3', 'Supervision Note (Demo)', 'Note for clinical supervision documentation', 'administrative', [
      { linkId: 's1', type: 'display', text: 'Supervision note', extension: headerExtension },
      { linkId: 'q1', type: 'text', text: 'Cases discussed' },
      { linkId: 'q2', type: 'text', text: 'Supervisor feedback and recommendations' },
    ], 'draft'),

    // Additional appointment templates (group / med mgmt) — ids preserved for mockAppointmentTemplates.service map
    mockRow('mock-note-3', 'Group Session Note (Demo)', 'Note template for group therapy sessions', 'appointments', [
      { linkId: 's1', type: 'display', text: 'Group session', extension: headerExtension },
      { linkId: 'q1', type: 'string', text: 'Group topic' },
      { linkId: 'q2', type: 'text', text: 'Group process and observations' },
      { linkId: 'q3', type: 'text', text: 'Individual participation notes' },
    ], 'draft'),
    mockRow('mock-note-4', 'Medication Management Note (Demo)', 'Focused note for medication review appointments', 'appointments', [
      { linkId: 's1', type: 'display', text: 'Medication review', extension: headerExtension },
      { linkId: 'q1', type: 'text', text: 'Current medications and adherence' },
      { linkId: 'q2', type: 'text', text: 'Side effects and tolerability' },
      { linkId: 'q3', type: 'text', text: 'Clinical assessment and plan' },
    ], 'draft'),
  ];
  return raw.map((t) => {
    const override = mockStatusOverrides.get(t.id);
    if (override) {
      return { ...t, status: override, resource: { ...t.resource, status: override } };
    }
    return t;
  });
}

/** True if the template id refers to mock data (not persisted on server). */
export function isMockTemplateId(id: string): boolean {
  return id.startsWith('mock-');
}

const mockStatusOverrides = new Map<string, 'active' | 'draft'>();

/** Toggle mock template status in-memory so the UI reflects checkbox state. */
export function updateMockTemplateStatus(id: string, status: 'active' | 'draft'): void {
  mockStatusOverrides.set(id, status);
}

/** Get the overridden status for a mock template, if any. */
export function getMockTemplateStatusOverride(id: string): 'active' | 'draft' | undefined {
  return mockStatusOverrides.get(id);
}

// ---------------------------------------------------------------------------
// Intake Configuration + pilot seed — same Questionnaire definitions as the UI
// ---------------------------------------------------------------------------

/** Canonical URL base for Template Library mock Questionnaires (used when `url` is omitted on the resource). */
export const MOCK_LIBRARY_QUESTIONNAIRE_URL_BASE = 'https://unify.health/fhir/Questionnaire';

export function mockQuestionnaireCanonicalUrl(templateId: string): string {
  return `${MOCK_LIBRARY_QUESTIONNAIRE_URL_BASE}/${templateId}`;
}

/**
 * **Single mock Questionnaire catalog** for Intake Configuration (mock Medplum), Template Library
 * fallback, and `rbac-pilot-seed`. Same definitions as {@link getMockTemplates}, with canonical
 * `url` + `version` on each resource. Pilot `PlanDefinition` intake rules reference these `mock-*` ids.
 */
export function getMockQuestionnairesForIntake(): Questionnaire[] {
  return getMockTemplates().map((t): Questionnaire => {
    const r = t.resource;
    const status: Questionnaire['status'] =
      t.status === 'retired' ? 'retired' : t.status === 'active' ? 'active' : 'draft';
    return {
      ...r,
      url: r.url ?? mockQuestionnaireCanonicalUrl(t.id),
      version: r.version ?? '1',
      status,
      name:
        r.name ??
        (r.title ? r.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') : t.id),
    };
  });
}
